// Lightweight read-only slide-out for previewing a customer's account when
// the kanban search popover's "View Account" button is clicked. Lives
// alongside the regular Slideout — that one is card-bound and renders
// timeline/composer/etc.; this one shows just account info + visit history.
//
// When the account has no active card (or only an archived one) the header
// surfaces a "Create card" / "Restore card" button beside Close. Clicking
// it POSTs to /api/accounts/:aid/ensure-lead-card and hands the resulting
// cardId back to App which opens the regular Slideout.
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAccountCluster } from "../hooks/useAccountCluster.js";
import { ensureLeadCardForAccount } from "../api.js";
import { PhoneLink } from "./PhoneLink.js";

type Tab = "Account" | "Visits";
const TABS: Tab[] = ["Account", "Visits"];

type Props = {
  aid: number | null;
  // Called when the slide-out is fully dismissed. If the user clicked
  // "Create card" / "Restore card", `cardId` is non-null and App should
  // open the regular Slideout for that card.
  onClose: (cardId: number | null) => void;
};

// Combine SCHEDULEDATE (YYYY-MM-DD) + STARTTIME (HH:MM[:SS]) into a single
// Date for display. Returns null if no date.
function parseScheduledAt(date: string | null, time: string): Date | null {
  if (!date) return null;
  const t = (time || "").trim();
  const hhmm = t.length >= 5 ? t.slice(0, 5) : "09:00";
  const d = new Date(`${date}T${hhmm}:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

// "Fri, 1 May 2026, 13:30" — used in the Visits list.
function fmtDayDateTime(d: Date): string {
  const date = d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${date}, ${time}`;
}

export function AccountSlideout({ aid, onClose }: Props): JSX.Element {
  const [tab, setTab] = useState<Tab>("Account");
  const [submitting, setSubmitting] = useState(false);
  const { data, isLoading } = useAccountCluster(aid);

  const accountName =
    data?.cluster.accounts[0]?.accountName ??
    (data?.cluster.contacts[0]
      ? data.cluster.contacts[0].fullName ||
        `${data.cluster.contacts[0].firstName} ${data.cluster.contacts[0].lastName}`.trim()
      : null) ??
    `Account #${aid ?? ""}`;

  // Show the "Create card" / "Restore card" button only once the cluster
  // has loaded; before then we don't yet know the card state. Active card
  // → no button.
  const cardState = data?.cardState ?? null;
  const cardCta: "create" | "restore" | null =
    !data ? null
      : data.cardState === null ? "create"
      : data.cardState.archived ? "restore"
      : null;

  function handleCloseRequest(): void {
    if (submitting) return;
    onClose(null);
  }

  async function handleCreateCard(): Promise<void> {
    if (aid === null || submitting) return;
    setSubmitting(true);
    try {
      const res = await ensureLeadCardForAccount(aid);
      onClose(res.cardId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("ensureLeadCardForAccount failed", err);
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {aid !== null ? (
        <>
          <motion.div
            key="acc-scrim"
            className="fixed inset-0 z-30 bg-ink-950/30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={submitting ? undefined : handleCloseRequest}
          />
          <motion.aside
            key="acc-panel"
            className="fixed right-0 top-0 z-40 flex h-full w-full max-w-[100vw] flex-col border-l border-ink-700 bg-ink-800 shadow-drag md:w-[42rem]"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 32 }}
          >
            <div
              className="h-[3px] w-full bg-slack-aubergineLight"
              aria-hidden
            />
            <header className="flex items-start justify-between gap-3 border-b border-ink-700 px-5 py-4">
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-slack-aubergineLight">
                  Preview · Account
                </div>
                <h2 className="mt-1 truncate text-2xl leading-tight text-ink-50">
                  {accountName}
                </h2>
                {cardState && cardState.archived ? (
                  <div className="mt-1 text-[12px] text-ink-200">
                    Card #{cardState.cardId} · archived
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {cardCta !== null ? (
                  <button
                    onClick={() => void handleCreateCard()}
                    disabled={submitting}
                    className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-2.5 py-1 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-50"
                    title={
                      cardCta === "create"
                        ? "Add this account to the kanban board (Lead column)"
                        : "Restore the archived card and move to Lead"
                    }
                  >
                    {submitting
                      ? "Working…"
                      : cardCta === "create"
                        ? "Create card"
                        : "Restore card"}
                  </button>
                ) : null}
                <button
                  onClick={handleCloseRequest}
                  disabled={submitting}
                  className="rounded-md border border-ink-500 bg-ink-800 px-2.5 py-1 text-[13px] text-ink-50 hover:border-ink-400 hover:bg-ink-700 disabled:opacity-50"
                  title="Close"
                >
                  Close
                </button>
              </div>
            </header>

            <nav className="flex gap-1 border-b border-ink-700 px-3 pt-2">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`rounded-t-md px-3 py-1.5 text-[13px] font-medium ${
                    tab === t
                      ? "border border-ink-700 border-b-ink-800 bg-ink-800 text-ink-50"
                      : "text-ink-200 hover:text-ink-50"
                  }`}
                >
                  {t}
                </button>
              ))}
            </nav>

            <div className="scrollbar-slim flex-1 overflow-y-auto px-5 py-4">
              {isLoading ? (
                <div className="text-[13px] text-ink-200">Loading…</div>
              ) : tab === "Account" ? (
                <AccountSection cluster={data?.cluster} />
              ) : (
                <VisitsSection cluster={data?.cluster} />
              )}
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function AccountSection({
  cluster,
}: {
  cluster: import("@breeze/shared").CardCluster | undefined;
}): JSX.Element {
  if (!cluster) return <div className="text-[13px] text-ink-200">No data.</div>;
  return (
    <div className="space-y-5">
      {cluster.accounts.length > 0 ? (
        <section>
          <h4 className="mb-1 text-[11px] uppercase tracking-[0.14em] text-ink-200">
            Accounts ({cluster.accounts.length})
          </h4>
          <ul className="space-y-1.5 text-[13px]">
            {cluster.accounts.map((a) => (
              <li
                key={a.aid}
                className="flex items-center gap-2 rounded-md border border-ink-700 bg-ink-900/40 px-2.5 py-1.5"
              >
                <span className="flex-1 truncate text-ink-50">
                  {a.accountName || `Account #${a.aid}`}
                </span>
                {a.pita ? (
                  <span className="rounded border border-slack-pink/50 bg-slack-pink/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slack-pink">
                    PITA
                  </span>
                ) : null}
                {a.dnc ? (
                  <span className="rounded border border-stage-chase/60 bg-stage-chase/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-stage-chase">
                    DNC
                  </span>
                ) : null}
                {a.dncMarketing ? (
                  <span className="rounded border border-ink-500 bg-ink-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-ink-200">
                    No marketing
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {cluster.contacts.length > 0 ? (
        <section>
          <h4 className="mb-1 text-[11px] uppercase tracking-[0.14em] text-ink-200">
            Contacts ({cluster.contacts.length})
          </h4>
          <ul className="space-y-1.5 text-[13px]">
            {cluster.contacts.map((c) => {
              const phones = [
                c.phoneMobile,
                c.phoneMain,
                c.phoneHome,
                c.phoneWork,
                c.phoneOther,
              ].filter(Boolean) as string[];
              return (
                <li
                  key={c.cid}
                  className="rounded-md border border-ink-700 bg-ink-900/40 px-2.5 py-1.5"
                >
                  <div className="font-medium text-ink-50">
                    {c.fullName ||
                      `${c.firstName} ${c.lastName}`.trim() ||
                      `Contact #${c.cid}`}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-ink-200">
                    {c.email ? <span>{c.email}</span> : null}
                    {phones.map((p) => (
                      <PhoneLink key={p} number={p} />
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {cluster.addresses.length > 0 ? (
        <section>
          <h4 className="mb-1 text-[11px] uppercase tracking-[0.14em] text-ink-200">
            Addresses ({cluster.addresses.length})
          </h4>
          <ul className="space-y-1.5 text-[13px]">
            {cluster.addresses.map((a) => (
              <li
                key={a.lid}
                className="rounded-md border border-ink-700 bg-ink-900/40 px-2.5 py-1.5 text-ink-50"
              >
                <span className="uppercase text-slack-cyan">{a.postcode}</span>
                <span className="ml-2 text-ink-200">
                  {[a.address1, a.address2, a.city].filter(Boolean).join(", ")}
                </span>
                {a.propertyType ? (
                  <span className="ml-2 text-ink-300">· {a.propertyType}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function VisitsSection({
  cluster,
}: {
  cluster: import("@breeze/shared").CardCluster | undefined;
}): JSX.Element {
  if (!cluster || cluster.visits.length === 0) {
    return <div className="text-[13px] text-ink-200">No visits on file.</div>;
  }
  return (
    <ul className="space-y-1.5 text-[13px]">
      {cluster.visits.map((v) => {
        const scheduled = parseScheduledAt(v.scheduleDate, v.startTime);
        return (
          <li
            key={v.vid}
            className="flex items-center gap-2 rounded-md border border-ink-700 bg-ink-900/40 px-2.5 py-1.5"
          >
            <span className="text-ink-200">
              {v.visitNumberText || `#${v.vid}`}
            </span>
            <span className="flex-1 truncate text-ink-50">
              {v.workOrderStatus || "—"}
            </span>
            {scheduled ? (
              <span className="text-ink-200">📅 {fmtDayDateTime(scheduled)}</span>
            ) : v.scheduleDate ? (
              <span className="text-ink-200">📅 {v.scheduleDate}</span>
            ) : null}
            {v.totalCost > 0 ? (
              <span className="text-ink-50">£{v.totalCost.toFixed(2)}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
